import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import { ServicesPage, ServiceDetail } from "@/pages/Services";
import QuotePage from "@/pages/Quote";
import TrackPage from "@/pages/Track";
import { CarriersPage, AgentsPage } from "@/pages/Partners";
import { InsightsPage, InsightDetail } from "@/pages/Insights";
import ContactPage from "@/pages/Contact";
import PrivacyPage from "@/pages/Privacy";
import { AdminProvider } from "@/components/admin/AdminContext";
import { AdminShell } from "@/components/admin/AdminShell";
import AdminLogin from "@/pages/admin/AdminLogin";
import PostsDashboard from "@/pages/admin/PostsDashboard";
import PostEditor from "@/pages/admin/PostEditor";
import PostPreview from "@/pages/admin/PostPreview";
import AccountSettings from "@/pages/admin/AccountSettings";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(err) {
    console.error(err);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-paper px-6">
          <div className="max-w-md text-left">
            <p className="label text-signal mb-3">Something went wrong</p>
            <p className="font-display text-2xl font-bold text-ink mb-3">This page hit a bump in the road.</p>
            <a href="/" data-testid="error-home-link" className="btn-pill primary-action px-6 py-3">
              Back to home
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll({ children }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true, touchMultiplier: 1.5 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.__lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);

  return children;
}

function SiteRoutes() {
  const admin = useLocation().pathname.startsWith("/admin");
  return (
    <>
          {!admin && <Navbar />}
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/track" element={<TrackPage />} />
            <Route path="/carriers" element={<CarriersPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightDetail />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/admin" element={<AdminProvider><AdminShell /></AdminProvider>}>
              <Route index element={<Navigate to="/admin/posts" replace />} />
              <Route path="login" element={<AdminLogin />} />
              <Route path="posts" element={<PostsDashboard />} />
              <Route path="posts/new" element={<PostEditor />} />
              <Route path="posts/:id/edit" element={<PostEditor />} />
              <Route path="posts/:id/preview" element={<PostPreview />} />
              <Route path="settings" element={<AccountSettings />} />
              <Route path="*" element={<Navigate to="/admin/posts" replace />} />
            </Route>
            <Route path="*" element={<Home />} />
          </Routes>
          {!admin && <><Footer /><CookieBanner /></>}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <SmoothScroll>
          <SiteRoutes />
        </SmoothScroll>
      </BrowserRouter>
      <Toaster position="bottom-right" />
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
